import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ShieldX, Home, ArrowLeft, AlertTriangle, Lock } from "lucide-react";

/**
 * Página de acceso no autorizado
 *
 * Se muestra cuando un usuario intenta acceder a una ruta
 * para la cual no tiene los permisos necesarios.
 * Optimizada para dispositivos móviles con layout responsive.
 */
const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full hover:shadow-lg transition-shadow">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
              <ShieldX className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            Acceso No Autorizado
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            No tienes permisos para acceder a esta página
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">
              Lo sentimos, pero no tienes los permisos necesarios para acceder a
              esta sección del sistema.
            </p>

            <div className="bg-muted/50 border border-border p-4 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground text-left">
                  Si crees que esto es un error, contacta al administrador del
                  sistema.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate("/")} className="w-full h-12">
              <Home className="h-4 w-4 mr-2" />
              Ir al Inicio
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="w-full h-12"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver Atrás
            </Button>
          </div>

          {/* Información adicional */}
          <div className="text-center pt-4 border-t border-border">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" />
              <span>Acceso restringido por seguridad</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnauthorizedPage;
